import React, { useState } from 'react';
import Style from './Governance.module.css';
import ConfirmPopup from '../common/popover/confirmPopup';
import { DispositionBadge, VersionBadge, STATUS_BADGE, domainFromPolicyName } from './GovernanceShared';

// ─────────────────────────────────────────────────────────────────────────────
// AppControls — the WEB/AI landing site list. Under the "policy = site" model each
// row is a SITE POLICY (its own version lifecycle), not an app-with-overrides.
// It NEVER reads the store: the caller (Governance landing) supplies `sites` and
// the navigation/delete handlers. Edit → per-site editor, Add → create mode,
// Delete → deleteGovernanceSite (confirmed).
//   controlType: 'WEB' | 'AI'
//   unsanctionedDiscoveredCount: WEB only (PIXMC-6083 §10.2) — the Unsanctioned Apps
//     discovery list's totalCount, supplied by WebControls (which does read the
//     store, just not this component). undefined for AI, where the middle card keeps
//     its original meaning (countBy('UNSANCTIONED') — authored Unsanctioned sites).
// ─────────────────────────────────────────────────────────────────────────────
const AppControls = ({ controlType, sites, onAddSite, onEditSite, onDeleteSite, unsanctionedDiscoveredCount }) => {
  const isAi = controlType === 'AI';
  const isWeb = controlType === 'WEB';
  const [confirmDelete, setConfirmDelete] = useState(null);

  const rows = Array.isArray(sites) ? sites : [];
  const countBy = (code) => rows.filter((s) => s.dispositionCode === code).length;

  const handleDeleteConfirmed = () => {
    const site = confirmDelete;
    setConfirmDelete(null);
    if (onDeleteSite) onDeleteSite(site);
  };

  // Per-site version cell (repurposed from the old "Data Controls" column): the
  // live published version + any open draft/approved version shown inline.
  const versionCell = (s) => {
    const pub = s.publishedVersion;
    const draft = s.openDraft;
    const draftStatus = draft ? (draft.statusCode || draft.StatusCode) : null;
    const draftNum = draft ? (draft.versionNumber ?? draft.VersionNumber) : null;
    return (
      <div>
        {pub
          ? <VersionBadge statusCode="PUBLISHED" versionNumber={pub.versionNumber ?? pub.VersionNumber} />
          : <span className={`${Style.badge} ${Style.neutral}`}>Not published</span>}
        {draft && (draftStatus === 'DRAFT' || draftStatus === 'APPROVED') && (
          <span className={Style.draftStateInline}>
            ({(STATUS_BADGE[draftStatus] || {}).label || draftStatus} v{draftNum})
          </span>
        )}
      </div>
    );
  };

  // Conditional access = the site's SANCTIONED-only email domains.
  const conditionalAccess = (s) => {
    if (s.dispositionCode !== 'SANCTIONED') return <span className={Style.dimCell}>—</span>;
    const list = Array.isArray(s.emailDomains) ? s.emailDomains : [];
    return list.length ? list.join(', ') : <span className={Style.dcSummaryDefault}>None</span>;
  };

  // Scan exemption (PIXMC-6437) — read-only badge, same pattern as Conditional
  // Access: valid for SANCTIONED and UNSANCTIONED (asymmetric from conditional
  // access, which is SANCTIONED-only), moot for DENIED (navigation is already
  // fully blocked there).
  const scanExemption = (s) => {
    if (s.dispositionCode === 'DENIED') return <span className={Style.dimCell}>—</span>;
    const engines = Array.isArray(s.noScanEngines) ? s.noScanEngines : [];
    return engines.includes('ALL')
      ? <span className={`${Style.badge} ${Style.moderate}`}>Exempt</span>
      : <span className={Style.dimCell}>—</span>;
  };

  const domainOf = (s) => s.appDomain || domainFromPolicyName(s.policyName);

  return (
    <div style={{ width: '100%' }}>
      <div className={Style.statRow3}>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>Sanctioned {isAi ? 'AI Apps' : 'Sites'}</div>
          <div className={Style.statValue}>{countBy('SANCTIONED')}</div>
          <div className={Style.statNote}>Approved with data controls configured</div>
        </div>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>{isWeb ? 'Unsanctioned Apps' : `Unsanctioned ${isAi ? 'AI Apps' : 'Sites'}`}</div>
          <div className={Style.statValue}>{isWeb ? (unsanctionedDiscoveredCount ?? 0) : countBy('UNSANCTIONED')}</div>
          <div className={Style.statNote}>{isWeb ? 'Detected, not yet approved' : 'Allowed with global defaults'}</div>
        </div>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>Denied {isAi ? 'AI Apps' : 'Sites'}</div>
          <div className={Style.statValue}>{countBy('DENIED')}</div>
          <div className={Style.statNote}>Blocked across organization</div>
        </div>
      </div>

      <div className={Style.card}>
        <div className={Style.cardHeader}>
          <div className={Style.cardTitle}>
            {isAi ? 'GenAI Application Policies' : 'Site & App Access Policies'}
          </div>
          <div className={Style.cardSubtitle}>
            {isAi
              ? 'Each GenAI app is its own policy, carrying a copy of the Data Controls rule set with per-app prompt / response controls and conditional access.'
              : 'Each site is its own policy, carrying a copy of the Data Controls rule set with per-site data controls and conditional access.'}
          </div>
        </div>
        <div className={Style.cardBody}>
          <table className={Style.table}>
            <thead>
              <tr>
                <th>{isAi ? 'App' : 'Site'}</th>
                <th>Policy</th>
                <th>Version</th>
                <th>Conditional Access</th>
                <th>Scan Exemption</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '24px' }}>
                    No {isAi ? 'AI apps' : 'sites'} configured yet
                  </td>
                </tr>
              )}
              {rows.map((s) => (
                <tr key={s.policyId}>
                  <td className={Style.domCell}>{domainOf(s)}</td>
                  <td>{s.dispositionCode ? <DispositionBadge code={s.dispositionCode} /> : <span className={Style.dimCell}>—</span>}</td>
                  <td>{versionCell(s)}</td>
                  <td>{conditionalAccess(s)}</td>
                  <td>{scanExemption(s)}</td>
                  <td>
                    <div className={Style.rowActions}>
                      <button className={Style.btn} onClick={() => onEditSite && onEditSite(s)}>Edit</button>
                      <button className={Style.btn} onClick={() => setConfirmDelete(s)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={Style.cardFooter}>
          <button className={`${Style.btn} ${Style.btnPrimary}`} onClick={() => onAddSite && onAddSite()}>
            {isAi ? 'Add AI App' : 'Add Site'}
          </button>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmPopup
          details={{
            icon: '', type: 'warning', title: `Delete ${isAi ? 'AI App' : 'Site'}?`,
            description: `Delete "${domainOf(confirmDelete)}" and its policy? This removes the site's published policy and any open draft. This cannot be undone.`,
            buttons: 'Y,N',
          }}
          onYesClick={handleDeleteConfirmed}
          onNoClick={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default AppControls;
